/*
 *
 *  * Copyright(c) 2022 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import * as _ from 'underscore';
import * as $moment from 'moment';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { DOCUMENT } from '@angular/common';
import { ChangeDetector } from '../../change-detector';
import { TranslateService } from '@ngx-translate/core';
import { isEmpty } from '../../helpers/helpers';
import { SearchPipe, SortPipe } from '../../pipes/pipes.module';
import { SortOrder } from '../../types/sort-order';

@Component({
  selector: 'wfm-virtual-list',
  templateUrl: './virtual-list.component.html',
  styleUrls: ['./virtual-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VirtualListComponent extends ChangeDetector implements OnChanges, AfterViewInit {
  @Input() items: any[];
  @Input() isShowSelected;
  @Input() rowHeight = 40;
  @Input() locale: string;
  @Input() labelField: string;
  @Input() sortOrder = SortOrder.ASC;
  @Input() multiple = false;
  @Input() headerVisible = false;
  @Input() enableInternalSearch = true;
  @Input() searchLabel;
  @Input() searchIcon;
  @Input() selectedItems: any[];
  @Input() disableByField: string | null = null;

  @Output() queryStr = new EventEmitter<any>();
  @Output() contentScrolled = new EventEmitter<number>();
  @Output() errorNotify = new EventEmitter<{ message: any; error: any }>();
  @Output() selectedItemsChange = new EventEmitter<Array<any>>();

  @ViewChild(CdkVirtualScrollViewport) viewport: CdkVirtualScrollViewport;
  @ViewChild('searchInput') searchInput: ElementRef<HTMLElement>;

  loading = false;
  filterText: string;
  displayedItems = [];
  itemsIndexMap = new Map<any, number>();
  selectedItemsMap = new Map<any, number>();
  showSelected = false;
  verticalOffsetSize = 0;
  verticalScrollSize = 0;
  verticalScrollPosition = 0;

  private scrolledByApplication = 0;
  private wheelTimer = 0;
  private wheelDelta = 0;
  private verticalScrollHandlerTimer = 0;
  private loadingTimeout;

  constructor(
    @Inject(DOCUMENT) private document: any,
    cd: ChangeDetectorRef,
    private translate: TranslateService,
    private searchPipe: SearchPipe,
    private sortPipe: SortPipe
  ) {
    super(document.defaultView, cd);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.locale && this.locale) {
      this.updateLocale();
    }

    if (changes.items) {
      this.items = this.items || [];
      this.createItemsIndexMap();
      this.setDisplayedItems();
      this.runScrollHandler();
    }

    if (changes.rowHeight) {
      this.calculateVerticalScroll();
    }

    if (changes.selectedItems) {
      this.setSelectedItems(this.selectedItems);
    }

    this.$detectChanges();
  }

  ngAfterViewInit(): void {
    this.searchLabel = !!this.searchLabel
      ? this.searchLabel
      : this.enableInternalSearch
      ? 'core.label.filterBy'
      : 'core.label.search';
  }

  focusInput() {
    this.searchInput?.nativeElement?.focus();
  }

  querySearchStr() {
    this.queryStr.emit(this.filterText);
    this.loading = true;

    this.loadingTimeout = this.window.setTimeout(() => {
      this.loading = false;
      this.clearLoadingTimout();
    }, 2000);
  }

  filterTextChange() {
    if (isEmpty(this.filterText?.trim())) {
      this.filterText = undefined;
    }

    this.setDisplayedItems();
    this.runScrollHandler();
  }

  selectAll() {
    if (this.displayedItems?.length) {
      const itemsToSelect = this.disableByField
        ? this.displayedItems.filter(item => !item[this.disableByField])
        : this.displayedItems;

      for (const item of itemsToSelect) {
        this.selectedItemsMap.set(item, this.itemsIndexMap.get(item));
      }
      this.selectedItemsChange.emit(Array.from(this.selectedItemsMap.keys()));
    }
  }

  deselectAll() {
    for (const item of this.displayedItems) {
      this.selectedItemsMap.delete(item);
    }
    this.selectedItemsChange.emit(Array.from(this.selectedItemsMap.keys()));
    if (this.showSelected) {
      this.showSelectedChange();
    }
  }

  toggleSelect(item: any) {
    if (this.selectedItemsMap.get(item) === undefined) {
      this.selectedItemsMap.set(item, this.itemsIndexMap.get(item));
    } else {
      this.selectedItemsMap.delete(item);
    }
    this.selectedItemsChange.emit(Array.from(this.selectedItemsMap.keys()));
    if (this.showSelected) {
      this.showSelectedChange();
    }
  }

  removeFilter(): void {
    this.filterText = '';
    if (this.enableInternalSearch) {
      this.setDisplayedItems();
    } else {
      this.items = this.selectedItems;
      this.calculateVerticalScroll();
      this.$detectChanges();
    }
  }

  showSelectedChange() {
    this.setDisplayedItems();
  }

  verticalScrollPositionChange(position: number) {
    this.verticalScrollPosition = position;
    this.scrollToOffset(position);
  }

  scrollToOffset(offset: number) {
    this.viewport.scrollToOffset(offset, 'auto');
    this.window.clearTimeout(this.scrolledByApplication);
    this.scrolledByApplication = this.window.setTimeout(() => {
      this.scrolledByApplication = 0;
    }, 500);
  }

  scrollHandler = ($event: any) => {
    this.contentScrolled.emit(this.verticalScrollPosition);

    if (this.scrolledByApplication && $event.target.scrollTop !== this.verticalScrollPosition) {
      $event.target.scrollTop = this.verticalScrollPosition;
      $event.stopPropagation();
      $event.preventDefault();
      return;
    }
  };

  onWheel($event: WheelEvent) {
    if (!$event.deltaY) {
      return;
    }

    $event.preventDefault();
    $event.stopPropagation();
    this.verticalScrollPosition += $event.deltaY;
    this.wheelDelta += $event.deltaY;

    this.verticalScrollPosition = Math.min(
      this.verticalScrollSize - this.verticalOffsetSize,
      Math.max(0, this.verticalScrollPosition)
    );

    if (!this.wheelTimer) {
      this.wheelTimer = this.window.setTimeout(() => {
        this.scrollToOffset(this.viewport.elementRef.nativeElement.scrollTop + this.wheelDelta);

        this.wheelTimer = 0;
        this.wheelDelta = 0;
      }, 100);
    }
  }

  private clearLoadingTimout() {
    if (this.loadingTimeout) {
      this.window.clearTimeout(this.loadingTimeout);
      this.loadingTimeout = undefined;
    }
  }

  private createItemsIndexMap() {
    let index = 0;
    this.itemsIndexMap.clear();
    if (this.items?.length) {
      for (const item of this.items) {
        this.itemsIndexMap.set(item, index++);
      }
    }
  }

  private updateLocale() {
    const reg = new RegExp(this.translate.getLangs().join('|'), 'g');
    if (this.locale.match(reg)) {
      this.translate.use(this.locale);
      $moment.locale(this.locale);
    }
  }

  private setDisplayedItems() {
    this.displayItemsThrottle();
  }

  private displayItemsThrottle = _.throttle(() => {
    this.loading = false;
    this.clearLoadingTimout();
    if (this.showSelected) {
      this.displayedItems = this.sortPipe.transform(
        this.searchPipe.transform(Array.from(this.selectedItemsMap.keys()), this.filterText, this.labelField) || [],
        this.sortOrder,
        this.labelField
      );
    } else {
      this.displayedItems = this.sortPipe.transform(
        this.searchPipe.transform(this.items, this.filterText, this.labelField) || [],
        this.sortOrder,
        this.labelField
      );
    }
    this.calculateVerticalScroll();
    this.$detectChanges();
  }, 1000);

  private setSelectedItems(items: any[]) {
    this.selectedItemsMap.clear();
    items = items || [];
    for (const item of items) {
      this.selectedItemsMap.set(item, this.itemsIndexMap.get(item));
    }
  }

  private runScrollHandler(interval = 0) {
    this.verticalScrollHandlerTimer && this.window.clearTimeout(this.verticalScrollHandlerTimer);
    this.verticalScrollHandlerTimer = this.window.setTimeout(() => {
      if (!this.wheelTimer) {
        this.verticalScrollPosition = this.viewport?.elementRef?.nativeElement?.scrollTop;
      }
    }, interval);
  }

  private verticalScrollCalculation;

  private calculateVerticalScroll() {
    this.verticalScrollCalculation && this.window.clearTimeout(this.verticalScrollCalculation);

    this.verticalScrollCalculation = this.window.setTimeout(() => {
      this.verticalOffsetSize = 0;
      this.verticalScrollSize = 0;
      this.verticalScrollPosition = 0;

      if (this.displayedItems?.length) {
        const element: HTMLElement = this.viewport.elementRef.nativeElement;
        this.verticalOffsetSize = element.offsetHeight;
        this.verticalScrollSize = element.scrollHeight;
        this.verticalScrollPosition = element.scrollTop;
      }

      this.verticalScrollCalculation = undefined;
      this.$detectChanges();
    }, 150);
  }
}
